/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * The user's widget auto-reload interval (minutes), persisted inside the widget preferences object
 * (`HG_WIDGET_PREFS.autoRefreshMinutes`). Pure storage helpers with no widget-bridge import, so the bridge, the
 * background-refresh registration and the settings screen can all share them without a cycle.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import {
    WIDGET_PREFS_KEY,
    WIDGET_REFRESH_OPTIONS,
    normalizeRefreshMinutes,
} from '../../config/widgetRefreshConfig';

/**
 * Read the stored auto-reload interval. Never throws.
 * @returns {Promise<number>} Minutes (one of `WIDGET_REFRESH_OPTIONS`).
 */
export const readAutoRefreshMinutes = async () => {
    try {
        const raw = await AsyncStorage.getItem(WIDGET_PREFS_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        return normalizeRefreshMinutes(parsed && parsed.autoRefreshMinutes);
    } catch (_error) {
        return normalizeRefreshMinutes(null);
    }
};

/**
 * Persist the auto-reload interval, merging over the stored preferences so the other keys survive. Never throws.
 * @param {number} minutes Requested minutes (normalised).
 * @returns {Promise<number>} The stored (normalised) minutes.
 */
export const writeAutoRefreshMinutes = async (minutes) => {
    const normalized = normalizeRefreshMinutes(minutes);
    try {
        const raw = await AsyncStorage.getItem(WIDGET_PREFS_KEY);
        const stored = raw ? JSON.parse(raw) : {};
        await AsyncStorage.setItem(WIDGET_PREFS_KEY, JSON.stringify({ ...(stored || {}), autoRefreshMinutes: normalized }));
    } catch (_error) {
        // Storage unavailable - the in-memory choice still applies for this session.
    }

    return normalized;
};

/**
 * The next interval in the cycling order.
 * @param {number} current Current minutes.
 * @returns {number}
 */
export const nextAutoRefreshMinutes = (current) => {
    const index = WIDGET_REFRESH_OPTIONS.indexOf(normalizeRefreshMinutes(current));

    return WIDGET_REFRESH_OPTIONS[(index + 1) % WIDGET_REFRESH_OPTIONS.length];
};
